import React from 'react';
import { IonButton, IonCol, IonGrid, IonIcon, IonInput, IonRow, IonToggle } from '@ionic/react';
import { addOutline, calendarOutline, timeOutline, trashOutline } from 'ionicons/icons';
import { PromocaoCatalogo, novaPromocao, dataPromocaoInput, dataPromocaoSalvar, mascararPrecoPromocional } from './promocaoCatalogo';
import './PromocaoProdutoEditor.css';

type Props = { value?: PromocaoCatalogo; onChange: (value: PromocaoCatalogo) => void; disabled?: boolean; nome?: string };
const PromocaoProdutoEditor: React.FC<Props> = ({ value, onChange, disabled, nome = 'produto' }) => {
  const update = (changes: Partial<PromocaoCatalogo>) => onChange({ ...(value || novaPromocao()), ...changes });
  const atualizarPreco = (event: any) => {
    const valor = mascararPrecoPromocional(event.detail?.value ?? event.target.value);
    // Atualiza também o controle para limpar caracteres recusados mesmo se o valor não mudar.
    event.target.value = valor;
    update({ valor });
  };
  const atualizarHorario = (index: number, field: 'inicio' | 'fim', horario: string) => update({
    horarios: (value?.horarios || []).map((h, i) => i === index ? { ...h, [field]: horario } : h)
  });
  return <fieldset className="catalogo-promocao" disabled={disabled}>
    <div className="catalogo-toggle-row">
      <div><div className="catalogo-toggle-label">{nome === 'produto' ? 'Ativar promoção' : `Promoção — ${nome}`}</div>
        <div className="catalogo-toggle-desc">O preço normal volta a valer fora do período da promoção.</div></div>
      <IonToggle aria-label={`Ativar promoção do ${nome}`} disabled={disabled} checked={value?.ativa === true}
        onIonChange={e => update({ ativa: e.detail.checked, ...(!value ? { valor: '' } : {}) })} />
    </div>
    {value && <>
      <div className="catalogo-promocao-grid">
        <div className="catalogo-field"><label className="catalogo-field-label">Preço promocional (R$)</label>
          <IonInput className="catalogo-field-input" type="tel" inputmode="numeric" maxlength={14} disabled={disabled}
            aria-label={`Preço promocional do ${nome}`} placeholder="0,00" value={value.valor}
            onIonInput={atualizarPreco} onIonChange={atualizarPreco} />
        </div>
        <div className="catalogo-field"><label className="catalogo-field-label">Início (opcional)</label>
          <IonInput className="catalogo-field-input" type="datetime-local" disabled={disabled}
            aria-label={`Início da promoção do ${nome}`} value={dataPromocaoInput(value.inicio)}
            onIonChange={e => update({ inicio: dataPromocaoSalvar(e.detail.value || '') })} />
        </div>
        <div className="catalogo-field"><label className="catalogo-field-label">Término (opcional)</label>
          <IonInput className="catalogo-field-input" type="datetime-local" disabled={disabled}
            aria-label={`Término da promoção do ${nome}`} value={dataPromocaoInput(value.fim)}
            onIonChange={e => update({ fim: dataPromocaoSalvar(e.detail.value || '') })} />
        </div>
      </div>
      <p className="catalogo-toggle-desc">Horário de Brasília. Sem início ou término, a promoção não tem limite de data.</p>
      <div className="catalogo-dias-card">
        <div className="catalogo-dias-card-header">
          <div className="catalogo-dias-card-icon"><IonIcon icon={calendarOutline} aria-hidden="true" /></div>
          <div><div className="catalogo-dias-card-title">Dias específicos</div>
            <div className="catalogo-dias-card-desc">Selecione os dias em que a promoção ficará ativa. Sem seleção, ela valerá todos os dias.</div></div>
        </div>
        <div className="catalogo-dias-chips">{['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((dia, index) => {
          const selecionado = (value.diasSemana || []).includes(index);
          return <button key={dia} type="button" disabled={disabled} aria-label={`${dia} na promoção do ${nome}`} aria-pressed={selecionado}
            className={`catalogo-dia-chip${selecionado ? ' catalogo-dia-chip--ativo' : ''}`}
            onClick={() => update({ diasSemana: selecionado ? value.diasSemana.filter(d => d !== index) : [...(value.diasSemana || []), index].sort() })}>{dia}</button>;
        })}</div>
      </div>
      <div className="catalogo-dias-card catalogo-promocao-horarios">
        <div className="catalogo-dias-card-header">
          <div className="catalogo-dias-card-icon"><IonIcon icon={timeOutline} aria-hidden="true" /></div>
          <div><div className="catalogo-dias-card-title">Horários específicos</div>
            <div className="catalogo-dias-card-desc">Adicione as faixas em que a promoção ficará ativa. Sem faixas, ela valerá o dia inteiro nos dias selecionados.</div></div>
        </div>
        {(value.horarios || []).map((horario, index) => <IonGrid key={index} className="catalogo-promocao-faixa"><IonRow>
          <IonCol size="5" sizeMd="4">
            <div className="catalogo-field"><label className="catalogo-field-label">Início</label>
              <IonInput className="catalogo-field-input" type="time" disabled={disabled} aria-label={`Horário inicial ${index + 1} da promoção do ${nome}`} value={horario.inicio}
                onIonChange={e => atualizarHorario(index, 'inicio', e.detail.value || '')} /></div>
          </IonCol>
          <IonCol size="5" sizeMd="4">
            <div className="catalogo-field"><label className="catalogo-field-label">Fim</label>
              <IonInput className="catalogo-field-input" type="time" disabled={disabled} aria-label={`Horário final ${index + 1} da promoção do ${nome}`} value={horario.fim}
                onIonChange={e => atualizarHorario(index, 'fim', e.detail.value || '')} /></div>
          </IonCol>
          <IonCol size="2" sizeMd="4" className="catalogo-promocao-faixa-acoes">
            <button type="button" className="catalogo-promocao-remover" disabled={disabled} aria-label={`Remover horário ${index + 1} da promoção do ${nome}`}
              onClick={() => update({ horarios: value.horarios.filter((_, i) => i !== index) })}><IonIcon icon={trashOutline} aria-hidden="true" /></button>
          </IonCol>
        </IonRow></IonGrid>)}
        <IonButton expand="block" fill="outline" size="small" className="catalogo-promocao-adicionar" disabled={disabled}
          onClick={() => update({ horarios: [...(value.horarios || []), { inicio: '', fim: '' }] })}>
          <IonIcon icon={addOutline} slot="start" />Adicionar faixa de horário
        </IonButton>
      </div>
    </>}
  </fieldset>;
};
export default PromocaoProdutoEditor;
